import { createApp } from 'vue';
import AppShellSkeleton from '@/shared/layouts/components/AppShellSkeleton/index.vue';
import { logger } from '@/shared/common/utils/logger.util';
import { DASHBOARD_ROUTE_NAMES } from '@/shared/common/constants/dashboardRoutes.constant';
import {
  getAppShellContentVariant,
  isAppShellSkeletonTarget,
} from './layoutSkeleton.util';
import { getStoredLayoutSkeletonVariant } from './layoutSkeletonVariantStorage.util';
import { routeUsesLayout } from './initialRoute.util';
import type {
  MountAppShellSkeletonParams,
  StartAppShellSkeletonParams,
} from '../types/mainLayout.type';

/**
 * La app real se monta recién cuando resuelve la primera navegación (guards incluidos):
 * hasta entonces este skeleton ocupa el contenedor, en una app aparte y sin router ni
 * stores. En la home arranca con la forma guardada para empalmar con el skeleton del
 * layout. Un fallo de render solo se loguea: el arranque no depende del skeleton.
 *
 * @returns función que lo desmonta y deja el contenedor vacío; no hace nada si no se montó
 */
export function mountAppShellSkeleton({
  container,
  isHomeRoute,
  hideNavbar,
  hideSidebar,
  ...target
}: MountAppShellSkeletonParams): () => void {
  if (!isAppShellSkeletonTarget(target)) return () => undefined;

  const variant = getAppShellContentVariant({
    isHomeRoute,
    storedVariant: isHomeRoute ? getStoredLayoutSkeletonVariant() : null,
  });

  const skeletonApp = createApp(AppShellSkeleton, { variant, hideNavbar, hideSidebar });
  skeletonApp.config.errorHandler = (error, _instance, info) => {
    logger.error(
      '[mountAppShellSkeleton] Error al dibujar el skeleton del shell',
      { info },
      error instanceof Error ? error : undefined
    );
  };
  skeletonApp.mount(container);

  return () => skeletonApp.unmount();
}

/**
 * Decide con la ruta inicial (ya con sus `redirect`) si el shell aplica. Saber si la ruta
 * dibuja MainLayout exige cargar su chunk, así que el shell aparece cuando ese chunk
 * llega; sin sesión o en una ruta pública ni siquiera lo pide.
 *
 * @returns función que lo detiene: desmonta el shell, o evita que se monte si la primera
 * navegación terminó antes de la decisión
 */
export function startAppShellSkeleton({
  hasSession,
  route,
  loadMainLayout,
  container,
}: StartAppShellSkeletonParams): () => void {
  const requiresAuth = route.matched.some((record) => record.meta?.requiresAuth);
  if (!hasSession || !requiresAuth) return () => undefined;

  let isStopped = false;
  let unmount: (() => void) | null = null;

  routeUsesLayout(route.matched, loadMainLayout).then((usesMainLayout) => {
    if (isStopped) return;

    unmount = mountAppShellSkeleton({
      hasSession,
      requiresAuth,
      usesMainLayout,
      hideNavbar: Boolean(route.meta.hideNavbar),
      hideSidebar: Boolean(route.meta.hideSidebar),
      isHomeRoute: route.name === DASHBOARD_ROUTE_NAMES.HOME,
      container,
    });
  });

  return () => {
    isStopped = true;
    unmount?.();
  };
}
